<template>
  <ModalDialog
    ref="dialog"
    title="Edit Stamp Config"
    :submitting="submitting"
    submitTitle="Update"
    icon="fa-image"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm" :labelColumnSize="2" v-if="!loading">
      <TabbedBox ref="tabBox">
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-stamp-config">
            <i class="far fa-cog"></i> Stamp Config
          </Tab>
          <Tab tabid="tab-stamp-autoapply">
            <i class="far fa-tools"></i> Auto Apply
          </Tab>
        </template>

        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-stamp-config">
            <FormUpdateFilter label="Price Group Filter" :options="priceGroupLookup" v-model="stampConfig.priceGroupFilter" field="priceGroupFilter" :editing="true" />

            <FormRow label="Stamp Required Stock" helpTip="The stock service used when stamps are required">
              <FormInputStockCode :stockType="InventoryStockTypes.glassService" v-model="stampConfig.stampRequiredStockId" />
            </FormRow>
            <FormRow label="Stamp Not Required Stock" helpTip="The stock service used when stamps are not required">
              <FormInputStockCode :stockType="InventoryStockTypes.glassService" v-model="stampConfig.stampNotRequiredStockId" />
            </FormRow>

            <FormHorizontalRule />

            <FormRow label="Stamp Location" :fieldCount="2">
              <template v-slot:field1>
                <FormSelect v-model="stampConfig.stampAlignHorz" :options="horizontalAlignmentLookup" field="stampAlignHorz" />
              </template>
              <template v-slot:field2>
                <FormSelect v-model="stampConfig.stampAlignVert" :options="verticalAlignmentLookup" field="stampAlignVert" />
              </template>
            </FormRow>

            <FormRow label="Stamp Size" :fieldCount="2">
              <template v-slot:field1>
                <FormInputText type="number" v-model="stampConfig.stampMaxSize" placeholder="Max Size" prepend="Max Size" addon="mm" field="stampMaxSize" note="Maximum size of the stamp. Final stamp size is adjusted to match image aspect ratio" />
              </template>
              <template v-slot:field2>
                <FormStaticLabel>
                  Stamp Size (HxW): <strong>{{stampConfig.stampHeight}}mm x {{stampConfig.stampWidth}}mm</strong>
                </FormStaticLabel>
              </template>
            </FormRow>

            <FormRow label="Stamp Image" helpTip="Stamp image displayed for the item on the panel. Import PNG files with transparency for best results">
              <FormImageCropper 
                ref="imageCropper"
                @imageUpdated="imageCropImageUpdated"
                @cropStarted="imageCropStarted"
                @cropUpdated="imageCropUpdated"
              />
            </FormRow>
          </TabPage>

          <TabPage tabid="tab-stamp-autoapply">
            <FormRow label="Auto Apply Stamps" helpTip="If set, will use the following rules to automatically add the stamp required / not required services. By default, stamps are always applied">
              <FormCheckbox v-model="stampConfig.isAutoApply" field="isAutoApply" />
            </FormRow>

            <div v-show="stampConfig.isAutoApply">
              <FormUpdateFilter label="No Stamps Customer Filter" :options="customerLookup" v-model="stampConfig.defaultStampNotRequiredContactFilter" field="defaultStampNotRequiredContactFilter" :editing="true" />
            </div>

          </TabPage>
        </template>
      </TabbedBox>
      
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import { InventoryStockTypes } from '@/store/types'
import { AlignmentTypes, AlignmentTypeLookup } from '@/store/mathTypes'

export default {
  name: 'OrderStampConfigDialog',
  mixins: [
    ModalDialogMixin,
    form,
  ],
  components: {
    ModalDialog,
    FormInputStockCode
  },
  props: {
  },
  watch: {
    'stampConfig.stampMaxSize': {
      handler: function() {
        this.recalculateStampSize()
      }
    }
  },
  data() {
    return {
      dialogForm: {},
      submitting: false,
      loading: false,
      loadingError: '',
      stampConfig: {
        stampMaxSize: 0,
        stampImageWidth: 0,
        stampImageHeight: 0,
        stampWidth: 0,
        stampHeight: 0,
      },
      selectedImage: {
        updated: false,
        width: 0,
        height: 0
      },
      customerLookup: [],
      priceGroupLookup: [],
    }
  },
  mounted() {
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
  },
  computed: {
    horizontalAlignmentLookup() {
      return AlignmentTypeLookup.filter(lookup => {
        return (
          lookup.value == AlignmentTypes.left || 
          lookup.value == AlignmentTypes.center || 
          lookup.value == AlignmentTypes.right
        )
      })
    },
    verticalAlignmentLookup() {
      return AlignmentTypeLookup.filter(lookup => {
        return (
          lookup.value == AlignmentTypes.top || 
          lookup.value == AlignmentTypes.middle || 
          lookup.value == AlignmentTypes.bottom
        )
      })
    }
  },
  methods: {
    show() {      
      this.dialogForm = this.clearFormResponse()
      this.submitting = false

      this.requestData()      

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.dialogForm = this.clearFormResponse()
      this.submitting = true

      var updateData = {
        stampAlignHorz: this.stampConfig.stampAlignHorz,
        stampAlignVert: this.stampConfig.stampAlignVert,
        stampRequiredStockId: this.stampConfig.stampRequiredStockId,
        stampNotRequiredStockId: this.stampConfig.stampNotRequiredStockId,
        stampMaxSize: this.stampConfig.stampMaxSize,
        stampWidth: parseInt(this.stampConfig.stampWidth || 0),
        stampHeight: parseInt(this.stampConfig.stampHeight || 0),

        isAutoApply: this.stampConfig.isAutoApply
      }

      if(this.stampConfig.priceGroupFilter)
        updateData.priceGroupFilter = this.stampConfig.priceGroupFilter
      if(this.stampConfig.isAutoApply && this.stampConfig.defaultStampNotRequiredContactFilter)
        updateData.defaultStampNotRequiredContactFilter = this.stampConfig.defaultStampNotRequiredContactFilter

      if(this.selectedImage.updated) {
        let imageData = this.$refs.imageCropper.getCroppedCanvasPNGUrl({
          minWidth: 16,
          minHeight: 16,
          maxWidth: 512,
          maxHeight: 512,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high'
        })

        updateData.stampImageData = imageData
        updateData.stampImageWidth = parseInt(this.selectedImage.width)
        updateData.stampImageHeight = parseInt(this.selectedImage.height)
      }

      this.$apiService.ordersService.updateStampConfig(updateData)
      .then(() => {
        this.submitting = false
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.submitting = false
        this.$data.dialogForm = this.processFormResponse(err)
      })
    },
    requestData() {
      this.loading = false
      this.loadingError = ''

      Promise.all([
        this.$apiService.contactService.getContactList({
          isActive: 1,
          isCustomer: 1
        }),
        this.$apiService.inventoryService.getStockList(),
        this.$apiService.pricingService.getStockPriceGroupList(),
        this.$apiService.ordersService.getStampConfig()
      ])
      .then(results => {
        this.customerLookup = results[0].data.map(item => {
          return { value: item.id, name: item.name }
        })
        this.stockLookup = results[1].data.filter(item => item.isActive).map(item => {
          return { value: item.id, name: item.code }
        })
        this.priceGroupLookup = results[2].data.filter(item => item.isActive).map(item => {
          return { value: item.id, name: item.name }
        })

        this.stampConfig = results[3].data
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    },
    imageCropStarted(data) {
      this.selectedImage.updated = true
    },
    imageCropImageUpdated() {
      this.selectedImage.updated = true
    },

    imageCropUpdated(data) {
      this.selectedImage.width = parseFloat(data.width || 0)
      this.selectedImage.height = parseFloat(data.height || 0)

      this.stampConfig.stampImageWidth = this.selectedImage.width
      this.stampConfig.stampImageHeight = this.selectedImage.height

      this.recalculateStampSize()
    },
    recalculateStampSize() {
      let maxSize = parseInt(this.stampConfig.stampMaxSize || 0)

      var scale = 1.0
      if(this.stampConfig.stampImageWidth > 0 && this.stampConfig.stampImageHeight > 0) {
        scale = Math.max(this.stampConfig.stampImageWidth / this.stampConfig.stampImageHeight, 0.001)

        if(scale >= 1.0) {
          this.stampConfig.stampWidth = Math.round(maxSize)
          this.stampConfig.stampHeight = Math.round(maxSize / scale)
        } else {
          this.stampConfig.stampWidth = Math.round(maxSize * scale)
          this.stampConfig.stampHeight = Math.round(maxSize)
        }
      }
    }
  }
}
</script>